//#region Util Imports

import * as moment from 'moment'
import * as _ from 'underscore'

//#endregion

//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import DataSource from 'devextreme/data/data_source'

//#endregion

import { TypeHelper } from '../../../helpers'
import { EventsNames } from '../../../helpers/events-names'
import { CommonHelper, Buttons, TextAlign, CorrelationType } from '../../../helpers/common-helper'
import { IUserSession } from '../../../helpers/user-session'
import { Permissions } from '../../../helpers/permissions'
import { Enums } from '../../../models/common/enums'
import { SystemService } from '../../../services/system'
import { GridContextMenuService } from '../../../services/grid'

import { DashboardGridDataModel, DashboardGridColumnModel } from '../../api/api.models'
import { GridOptions, GridTypes } from '../../common/grid/grid.controller'
import { Api, LocalDictionaryFactory } from '../../api/api.module'
import { BaseGridController, ScopeBaseGrid } from '../../common/common.base.grid.controller'
import { GridDetailsController, GridDetailsSettings } from '../../common/grid-details/grid-details.directive.controller'
import { TableController as Table } from '../../common/common.table.controller'

import { DashboardRefreshControlDelay, DashboardDelay } from '../dashboard.controller'

import { ExportFormatValueArgs } from '../../../modules/common/export'

interface TableScope extends ScopeBaseGrid {
    options: GridOptions
    datagrid: DevExpress.ui.dxDataGrid
    fitToScreen: boolean
    isEditMode: boolean
    needLoader: boolean
    toolTipOptions: DevExpress.ui.dxTooltipOptions
    tooltipVisible: boolean
    toolTipText: string

    data: DashboardGridDataModel
    shortDetailsSettings: GridDetailsSettings
}

export class TableController extends BaseGridController {
    private static lastOnInitializedCallback: ng.IPromise<void> = null
    private outsideChanges: boolean = false

    constructor(
        protected readonly $scope: TableScope,
        protected readonly $q: ng.IQService,
        protected readonly $timeout: ng.ITimeoutService,
        protected readonly $state: StateService,
        protected readonly api: Api,
        protected readonly localDictionary: LocalDictionaryFactory,
        protected readonly gridContextMenuService: GridContextMenuService,
        protected readonly userSession: IUserSession,
        protected readonly systemService: SystemService
    ) {
        super($scope, $timeout, $state, userSession, systemService, api, localDictionary, (<DashboardGridDataModel>$scope['data']).referenceType, (<DashboardGridDataModel>$scope['data']).referenceType)
        $scope.$on('$destroy', function () {
            if (TableController.lastOnInitializedCallback) {
                $timeout.cancel(TableController.lastOnInitializedCallback)
            }
        });

        var data: DashboardGridDataModel = $scope['data']

        if (data == null) {
            console.error('Data can\'t be null in dashboard table control')
        }

        $scope.$on(EventsNames.DASHBOARD_UPDATE_TABLE_SOURCE + $scope.data.id, (event, ...args: any[]) => {
            if (this.dataGrid) {
                const dataSource: any[] = args[0]
                let id: number = 0
                if (dataSource && dataSource.length > 0) {
                    dataSource.forEach(row => {
                        if (!row.id) {
                            row.id = id++
                        }
                        parseCellColor(row);
                    })
                }
                this.dataGrid.option('dataSource', dataSource)
            }
        })

        if (data.shortDetailsEnabled && $scope.data.referenceType) {
            $scope.shortDetailsSettings = GridDetailsController.getOptionsByReferenceType($scope.data.referenceType)
            $scope.shortDetailsSettings.measurmentIdSaveTo = "DashboardTableController" + $scope.data.referenceType
        }

        $scope.$on(EventsNames.DASHBOARD_UPDATE, (event, ...args) => {
            const delay: DashboardDelay = args[0]
            delay.time += DashboardRefreshControlDelay
            this.$timeout(() => {
                try {
                    if ($scope.datagrid) {
                        $scope.datagrid.updateDimensions()
                    }
                } catch (e) {
                    console.warn('Unable update dimensions grid.')
                }
            }, delay.time)
        })

        const getColumn = (c: DashboardGridColumnModel, unit: string = '') => {
            if (c.dataField == 'referencevaluearray' || c.dataField == 'rowcolor' || c.dataField == 'rowtooltip' || c.dataField == 'rowtooltipparams' || c.dataField == 'cellcolor') c['visible'] = false
            else c['visible'] = true

            if (unit) unit = '[' + unit + ']'

            let type = 'string'
            let colType = CommonHelper.string
            if (c.type == 'System.Int64' || c.type == 'System.Int32' || c.type == 'System.Double') {
                type = 'number'
                if (c.type == 'System.Int64' || c.type == 'System.Double') {
                    colType = Enums.DataTypeClass.Real
                }
                if (c.type == 'System.Int32') {
                    colType = Enums.DataTypeClass.Integer
                }
            }
            if (c.type == 'System.Boolean') {
                type = 'boolean'
                colType = Enums.DataTypeClass.Boolean
            }

            const column = {
                caption: c.caption ? c.caption.description + ' ' + unit : '' + unit,
                dataField: c.dataField,
                dataType: type,
                visible: c['visible'],
                columnType: colType,
                idDataTypeFormat: c.idDataTypeFormat,
            }

            if (c.type == "System.DateTime") {
                column.dataType = 'date'
                column.columnType = CommonHelper.datetime
                column['customizeText'] = (options: any) => {
                    if (options.value)
                        return moment(options.value).format(window['simax'].getDateFormat('DATE_TIME_FORMAT_MOMENTJS'))
                    return ''
                }
                //the same as in history.directive
                column['editorOptions'] = Table.getColumnEditorOptions('0', CommonHelper.datetime, $timeout)
                Table.addCustomFiltersToDxColumn(column, 'date', { type: CommonHelper.datetime }, false, null, null, null)
            }
            return column
        }

        var getColumnDefered = (column: DashboardGridColumnModel) => {
            var defer = $q.defer()
            defer.resolve(getColumn(column))
            return defer.promise
        }

        var createTooltip = (target: any) => {
            $scope.toolTipOptions = null
            $timeout(() => {
                $scope.toolTipOptions = {
                    target: target,
                    bindingOptions: {
                        visible: 'tooltipVisible'
                    },
                    onInitialized: e => {
                        e.component.option({ target: target })
                    }
                }
            })
        }

        var scrollToSelected = (component) => {
            var scrollable = component.getView('rowsView')._scrollable
            var selectedRowElements = component.element().find('tr.dx-selection')
            var gridHeight = component.getView('gridView')._rootElement[0].clientHeight
            if (selectedRowElements && selectedRowElements[0] && scrollable)
                scrollable.scrollTo(selectedRowElements[0].offsetTop - (gridHeight / 4))
        }

        var getMenuItems = (isTouchScreen: boolean = false) => {
            const selectedRows: any = this.dataGrid.getSelectedRowsData()
            const ids: number[] = selectedRows.map((selectedRow: any) => {
                return !selectedRow['referencevaluearray'] ? selectedRow[CommonHelper.idField] : selectedRow['referencevaluearray'][0]
            })

            const showProblemConfirmation: boolean = data.buttonOptions.some(item => { return Buttons.ProblemConfirmation == item.option && item.visible })
            const showPlanDelivery: boolean = data.buttonOptions.some(item => { return Buttons.PlanDelivery == item.option && item.visible })

            switch (data.referenceType) {
                case Enums.ReferenceType.IdAlarmEvent:
                    return this.gridContextMenuService.getContextMenuItemsByReferenceType(Enums.ReferenceType.IdAlarmEvent, {
                        idReferenceTypes: ids,

                        onConfirmAlarm: () => {
                            this.onAlarmDoneMenuClick()
                        },
                    })

                case Enums.ReferenceType.IdDeviceConnection:
                    return this.gridContextMenuService.getContextMenuItemsByReferenceType(Enums.ReferenceType.IdDeviceConnection, {
                        idReferenceTypes: ids,

                        showParentDetails: () => {
                            this.showRadioDeviceDetails({ data: this.dataGrid.getSelectedRowsData()[0] })
                        },

                        showDetails: () => {
                            this.$scope.showDetails({ data: this.dataGrid.getSelectedRowsData()[0] })
                        },

                        onShowDevices: () => {
                            this.onShowHubsDetailsPreview()
                        },

                        onShowParentDevices: () => {
                            this.onShowRadioDevicesDetailsPreview()
                        },

                    })

                case Enums.ReferenceType.IdLocation:
                    return this.gridContextMenuService.getContextMenuItemsByReferenceType(Enums.ReferenceType.IdLocation, {
                        idReferenceTypes: ids,

                        getCorrespondingActionType: (idReferenceType: Enums.ReferenceType, e: any) => {
                            this.getCorrespondingActionType(idReferenceType, e)
                        },

                        getLocationsProblems: showProblemConfirmation ? () => this.getLocationsProblems(data.id) : null,
                        planDelivery: showPlanDelivery ? () => this.planDelivery(data.id) : null,
                    })

                case Enums.ReferenceType.IdMeter:
                    return this.gridContextMenuService.getContextMenuItemsByReferenceType(Enums.ReferenceType.IdMeter, {
                        idReferenceTypes: ids,

                        getCorrespondingActionType: (idReferenceType: Enums.ReferenceType, e: any) => {
                            this.getCorrespondingActionType(idReferenceType, e)
                        }
                    })

                case Enums.ReferenceType.IdTask:
                    return this.gridContextMenuService.getContextMenuItemsByReferenceType(Enums.ReferenceType.IdTask, {
                        idReferenceTypes: ids,

                        showStatusChangePopup: () => {
                            this.showTaskStatusPopup()
                        }
                    })

                case Enums.ReferenceType.SerialNbr:
                    return this.gridContextMenuService.getContextMenuItemsByReferenceType(Enums.ReferenceType.SerialNbr, {
                        idReferenceTypes: ids,
                        getCorrespondingActionType: (idReferenceType: Enums.ReferenceType, e: any) => {
                            this.getCorrespondingActionType(idReferenceType, e)
                        }
                    })
            }
        }

        const parseCellColor = (row) => {
            if (row.cellcolor) {
                let colors: { [column: string]: string } = {}
                let tmpCellColor: string[] = row.cellcolor.split(',')
                tmpCellColor.forEach(tcc => {
                    tcc = tcc.substring(1, tcc.length - 1)
                    let tmp: string[] = tcc.split(':')
                    colors[tmp[0].toLowerCase()] = aHexToRgbA(tmp[1])
                })
                for (let prop in row) {
                    for (let column in colors) {
                        if (prop.toLowerCase().indexOf(column.toLowerCase()) > -1)
                            colors[prop] = colors[column]
                    }
                }
                row.colors = colors
            }
        }

        const preCreateGrid = () => {
            $scope.needLoader = true
            createGrid()
        }
        const createGrid = _.debounce(() => {
            data = $scope['data']

            if (data && data.columns) {
                $scope.needLoader = true
                const columns = data.columns.map(c => {
                    if (c.idDataTypeFormat) {
                        return localDictionary.getDataTypes([c.idDataTypeFormat]).then(dts => {
                            if (dts && dts.length > 0) {
                                return getColumn(c, dts[0].unitName)
                            } else {
                                return getColumn(c)
                            }

                        })
                    } else {
                        return getColumnDefered(c)
                    }
                })

                $q.all(columns).then((cols: any) => {
                    cols = cols.map((c: any, index: number) => {
                        if (c.dataType == 'date' && angular.isArray(data.dataSource)) {
                            var values = data.dataSource.filter(d => d[c.dataField] != null && d[c.dataField] != undefined)
                            if (values.length > 0) {
                                c['serializationFormatValue'] = values[0][c.dataField]
                            }
                        }
                        c.visibleIndex = index
                        return c
                    })

                    $scope.needLoader = false
                    $scope.options = null
                    $timeout(() => {
                        if (data.dataSource) {
                            // update colors and id
                            var id = 0
                            data.dataSource.forEach(row => {
                                if (!row.id) row.id = id++
                                parseCellColor(row);
                            })
                        }

                        $scope.options = {
                            name: "tableGrid",
                            idReferenceType: Enums.ReferenceType.None,
                            type: GridTypes.PAGINATED_SMALL,
                            buttonsOptions: {
                                enabled: true,
                                dashboardSettings: data.buttonOptions,
                                problemConfirmationPermissions: "['" + Permissions.LOCATION_PROBLEM_CONFIRMATION_OP + "']",
                                planDeliveryPermissions: "['" + Permissions.LOCATION_PLAN_DELIVERY_TIME_OP + "']",
                                onProblemConfirmationClick: () => { this.getLocationsProblems(data.id) },
                                onPlanDeliveryClick: () => { this.planDelivery(data.id) }
                            },
                            columns: cols,
                            dataSource: new DataSource(data.dataSource),
                            onInitialized: (e) => {
                                $scope.datagrid = e.component
                                this.dataGrid = e.component
                                if (TableController.lastOnInitializedCallback) {
                                    $timeout.cancel(TableController.lastOnInitializedCallback)
                                }
                                TableController.lastOnInitializedCallback = $timeout(() => {
                                    try {
                                        this.dataGrid.repaint()
                                    } catch (e) {
                                        console.warn('Problem in table ', e)
                                    }
                                }, 1000)
                            },
                            exportOptions: {
                                exportDataMethod: this.userSession.getDataProvider().Dashboard.exportOperatorDashboardData,
                                referenceType: Enums.ReferenceType.None,

                                formatValue: (e: ExportFormatValueArgs) => {
                                    if (TypeHelper.isMSDate(e.value) || _.isDate(e.value)) {
                                        return moment(e.value).format(window['simax'].getDateFormat('DATE_TIME_FORMAT_MOMENTJS'))
                                    }

                                    return e.value
                                }
                            },
                            height: '100%',
                            onRowDoubleClick: (e) => {
                                if (data.referenceType && e.data.referencevaluearray && e.data.referencevaluearray.length) {
                                    let array: number[]
                                    if (angular.isString(e.data.referencevaluearray)) {
                                        array = e.data.referencevaluearray.split(';').filter((e) => { return e === 0 || e })
                                    }
                                    else {
                                        array = e.data.referencevaluearray
                                    }

                                    this.systemService.openDetailsOrModule({
                                        idReferenceType: data.referenceType,
                                        referenceObjectIds: array || []
                                    })
                                }
                            },
                            onSelectionChanged: e => {
                                if (data.referenceType && !this.outsideChanges) {
                                    $timeout(() => {
                                        var referenceData: any[] = []
                                        e.selectedRowsData.forEach(item => {
                                            let array: number[]
                                            if (angular.isString(item.referencevaluearray)) {
                                                array = item.referencevaluearray.split(';').filter((e) => { return e === 0 || e })
                                            }
                                            else {
                                                array = item.referencevaluearray
                                            }

                                            if (array) {
                                                array.forEach(item => {
                                                    referenceData.push(item)
                                                })
                                            }
                                        })
                                        if ($scope.shortDetailsSettings) {
                                            $scope.shortDetailsSettings.objects = referenceData.map(d => {
                                                return { id: d }
                                            })
                                        }
                                        $scope.$root.$broadcast(EventsNames.DASHBOART_GRID_ROW_SELECTED, referenceData, data.referenceType)
                                    })
                                } else {
                                    scrollToSelected(e.component)
                                }
                                this.outsideChanges = false
                            },
                            onRowPrepared: (e: any) => {
                                if (['header', 'filter', 'totalFooter'].indexOf(e.rowType) < 0 && e.data && e.data.rowcolor) {
                                    if (e.data.rowcolor >= 0) {
                                        e.rowElement.find('td:not(.cell-colored)').css('background-color', e.data.rowcolor)
                                    } else {
                                        e.rowElement.find('td:not(.cell-colored)').css('background-color', e.data.rowcolor)
                                    }
                                }
                                if (e.rowType == 'header') {
                                    switch (data.headerTextAlign) {
                                        case TextAlign.Left:
                                            e.rowElement.find('td').css('text-align', 'left')
                                            break
                                        case TextAlign.Right:
                                            e.rowElement.find('td').css('text-align', 'right')
                                            break
                                        case TextAlign.Center:
                                            e.rowElement.find('td').css('text-align', 'center')
                                            break
                                    }
                                }
                            },
                            onCellPrepared: (e: any) => {
                                if (e.column.dataField && e.rowType == 'data' && e.data.colors && e.data.colors[e.column.dataField]) {
                                    e.cellElement.css('background-color', e.data.colors[e.column.dataField]).addClass('cell-colored')
                                }
                            },
                            onCellHoverChanged: e => {
                                if (e.rowType == 'data') {
                                    if (e.data.rowtooltip) {
                                        if (e.eventType == 'mouseover') {
                                            createTooltip(e.cellElement)
                                            $scope.toolTipText = e.data.rowtooltip
                                            $scope.tooltipVisible = true
                                        }
                                        if (e.eventType == 'mouseout') {
                                            $scope.tooltipVisible = false
                                        }
                                    }
                                }
                                if (e.rowType == 'header') {
                                    var tooltiptext = data && data.columns[e.column.index] && data.columns[e.column.index].tooltip ? data.columns[e.column.index].tooltip.description : ''
                                    if (tooltiptext) {
                                        if (e.eventType == 'mouseover') {
                                            createTooltip(e.cellElement)
                                            $scope.toolTipText = tooltiptext
                                            $scope.tooltipVisible = true
                                        }
                                        if (e.eventType == 'mouseout') {
                                            $scope.tooltipVisible = false
                                        }
                                    }
                                }
                            },
                            referenceId: data.referenceType
                        }
                        if (data.contextMenuEnabled)
                            $scope.options.contextMenuItems = (isTouchScreen: boolean = false) => {
                                return getMenuItems(isTouchScreen)
                            }
                    })
                })
            }
            else {
                $scope.options = null
                $timeout(() => {
                    $scope.options = {
                        name: "tableGrid",
                        idReferenceType: Enums.ReferenceType.None,
                        type: GridTypes.PAGINATED_SMALL,
                        buttonsOptions: {
                            enabled: true
                        },
                        columns: null,
                        onInitialized: (e) => { $scope.datagrid = e.component },
                        onContentReady: () => {
                            $scope.datagrid.option('pager.visible', true)
                            $scope.datagrid.updateDimensions()
                        },
                        height: $scope.fitToScreen ? '100%' : '350px'
                    }
                })
            }
        }, 1000)

        preCreateGrid()

        $scope.$on(EventsNames.UPDATE_DASHBOARD_ITEM + data.id, (event, ...args) => {
            preCreateGrid()
        })

        var lastMarkerClickedTimeout: ng.IPromise<void> = null

        $scope.$on(EventsNames.DASHBOARD_MAP_MARKER_CLICKED, (event: ng.IAngularEvent, ...args: any[]) => {
            var selectedMarker = args[0]
            var referenceType: number = args[1]
            var correlationType: number = data.correlationType

            if (correlationType == CorrelationType.None) return
            if (correlationType == CorrelationType.Local) {
                if (referenceType == $scope['data'].referenceType) {
                    $scope.datagrid.clearSelection()
                    if (this.dataGrid) {
                        var dataSource = this.dataGrid.getDataSource()
                        var loadOptions: any = dataSource.loadOptions()
                        loadOptions.filter = this.dataGrid.getCombinedFilter()
                        dataSource.store().load(loadOptions).done(items => {
                            var pageSize = $scope.datagrid.pageSize();
                            var selectedRowIndex = -1;
                            for (var i = 0; i < items.length; i++) {
                                if (items[i].referencevaluearray) {
                                    if (items[i].referencevaluearray[0] !== selectedMarker.id) continue;
                                    selectedRowIndex = i;
                                }
                                break;
                            }
                            if (selectedRowIndex > -1) {
                                if (lastMarkerClickedTimeout) $timeout.cancel(lastMarkerClickedTimeout)

                                var scrollable = $scope.datagrid['getView'] && $scope.datagrid.option('columnAutoWidth') ? $scope.datagrid['getView']('rowsView')._scrollable : null
                                var lastScrollLeft = scrollable ? scrollable.scrollLeft() : 0
                                if (lastScrollLeft == -0) lastScrollLeft = 0
                                var pageIndex = Math.floor(selectedRowIndex / pageSize)
                                $scope.datagrid.pageIndex(pageIndex)
                                var item = items[selectedRowIndex]

                                if ($scope.shortDetailsSettings && item.referencevaluearray && item.referencevaluearray[0]) {
                                    $scope.shortDetailsSettings.objects = [{ id: item.referencevaluearray[0] }]
                                }

                                var promise: any = $scope.datagrid.selectRows([item.id], false)
                                if (promise && promise.promise) {
                                    promise.promise().then(() => {
                                        if (scrollable) {
                                            // no delay
                                            lastMarkerClickedTimeout = $timeout(() => {
                                                scrollable.scrollTo({ left: lastScrollLeft, top: scrollable.scrollTop() })
                                                // laggy
                                                lastMarkerClickedTimeout = $timeout(() => {
                                                    scrollable.scrollTo({ left: lastScrollLeft, top: scrollable.scrollTop() })
                                                    // long lag
                                                    lastMarkerClickedTimeout = $timeout(() => {
                                                        scrollable.scrollTo({ left: lastScrollLeft, top: scrollable.scrollTop() })
                                                        // dla pewno�ci
                                                        lastMarkerClickedTimeout = $timeout(() => {
                                                            scrollable.scrollTo({ left: lastScrollLeft, top: scrollable.scrollTop() })
                                                            // dla Kamili
                                                            lastMarkerClickedTimeout = $timeout(() => {
                                                                scrollable.scrollTo({ left: lastScrollLeft, top: scrollable.scrollTop() })
                                                            }, 3000)
                                                        }, 1000)
                                                    }, 500)
                                                }, 500)
                                            }, 0)
                                        }
                                        this.outsideChanges = true
                                    })
                                } else {
                                    this.outsideChanges = true
                                }
                            }
                        })
                    }
                }
            }
            if (correlationType == CorrelationType.Server) {
                //isnt used for grid for now
            }
        })

        $scope.$on(EventsNames.DASHBOARD_SLIDER_SLICKED, (event: ng.IAngularEvent, ...args: any[]) => {
            var slider = args[0]
            var correlationType: number = data.correlationType

            if (correlationType == CorrelationType.None) return
            if (correlationType == CorrelationType.Local) {
                if (slider.referenceType == $scope['data'].referenceType) {
                    $scope.datagrid.clearSelection()
                    $scope.datagrid.getDataSource().items().forEach(item => {
                        if (item.referencevaluearray) {
                            item.referencevaluearray.forEach(id => {
                                if (id == slider.idObject) {
                                    $scope.datagrid.selectRows([item], true)
                                    this.outsideChanges = true
                                }
                            })
                        }
                    })
                }
            }
            if (correlationType == CorrelationType.Server) {
                //isnt used for grid for now
            }
        })

        var aHexToRgbA = (hex) => {
            var c;
            if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
                c = hex.substring(1).split('');
                if (c.length == 4) {
                    c = [c[0], c[0], c[1], c[1], c[2], c[2]];
                }
                c = '0x' + c.join('');
                return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',1)';
            } else if (/^#([A-Fa-f0-9]{4}){1,2}$/.test(hex)) {
                c = hex.substring(1).split('');
                if (c.length == 4) {
                    c = [c[0], c[0], c[1], c[1], c[2], c[2], c[3], c[3]];
                }
                c = '0x' + c.join('');
                return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255, Math.floor((((c >> 24) & 255) / 255) * 100) / 100].join(',') + ')';
            }
            throw new Error('Bad Hex');
        }

    }
}